import { useState, useEffect } from "react";
import { useRoute } from "wouter";
import { Loader2, CheckCircle, ChevronRight, ChevronLeft, Zap, Shield, TrendingUp, Star, HelpCircle, ArrowRight, BarChart3, Users, Clock, Target, Award, Heart, Globe, Sparkles, Send, Quote, MessageCircle, X } from "lucide-react";
import type { LandingPageSection, FunnelStep, LandingPageData, LandingPageSettings } from "@shared/schema";

interface PageTheme {
  primaryColor: string;
  bgColor: string;
  txtColor: string;
  buttonColor: string;
  cardBg: string;
  cardBorder: string;
  inputBg: string;
  inputBorder: string;
  mutedText: string;
}

function normalizeHex(val: string | undefined, fallback: string): string {
  if (!val) return fallback;
  const v = val.trim();
  const m6 = v.match(/^#([0-9a-fA-F]{6})$/);
  if (m6) return v;
  const m3 = v.match(/^#([0-9a-fA-F]{3})$/);
  if (m3) return `#${m3[1][0]}${m3[1][0]}${m3[1][1]}${m3[1][1]}${m3[1][2]}${m3[1][2]}`;
  return fallback;
}

function buildTheme(theme?: LandingPageData['theme']): PageTheme {
  const pc = normalizeHex(theme?.primaryColor, "#3B82F6");
  const bg = normalizeHex(theme?.backgroundColor, "#0A0A0F");
  const txt = normalizeHex(theme?.textColor, "#FFFFFF");
  const btn = normalizeHex(theme?.buttonColor, pc);
  const isDark = isColorDark(bg);
  const cardBgFromTheme = theme?.cardBackground ? normalizeHex(theme.cardBackground, "") : "";
  return {
    primaryColor: pc,
    bgColor: bg,
    txtColor: txt,
    buttonColor: btn,
    cardBg: cardBgFromTheme || (isDark ? lighten(bg, 6) : darken(bg, 4)),
    cardBorder: isDark ? `${txt}12` : `${txt}18`,
    inputBg: isDark ? `${txt}08` : `${txt}06`,
    inputBorder: isDark ? `${txt}15` : `${txt}20`,
    mutedText: isDark ? `${txt}99` : `${txt}88`,
  };
}

function isColorDark(hex: string): boolean {
  const c = hex.replace("#", "");
  if (c.length < 6) return true;
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

function lighten(hex: string, pct: number): string {
  const c = hex.replace("#", "");
  if (c.length < 6) return hex;
  const r = Math.min(255, parseInt(c.substring(0, 2), 16) + Math.round(255 * pct / 100));
  const g = Math.min(255, parseInt(c.substring(2, 4), 16) + Math.round(255 * pct / 100));
  const b = Math.min(255, parseInt(c.substring(4, 6), 16) + Math.round(255 * pct / 100));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function darken(hex: string, pct: number): string {
  const c = hex.replace("#", "");
  if (c.length < 6) return hex;
  const r = Math.max(0, parseInt(c.substring(0, 2), 16) - Math.round(255 * pct / 100));
  const g = Math.max(0, parseInt(c.substring(2, 4), 16) - Math.round(255 * pct / 100));
  const b = Math.max(0, parseInt(c.substring(4, 6), 16) - Math.round(255 * pct / 100));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

type IconComponent = typeof Zap;
const iconMap: Record<string, IconComponent> = {
  Zap, Shield, TrendingUp, Star, HelpCircle, CheckCircle, ArrowRight, BarChart3, Users, Clock, Target, Award, Heart, Globe, Sparkles, Send, Loader2, Quote,
};

function getIcon(name?: string, size = 20) {
  if (!name) return null;
  const Icon = iconMap[name];
  return Icon ? <Icon size={size} /> : null;
}

function HeroSection({ section, t }: { section: LandingPageSection; t: PageTheme }) {
  const hasBgImage = !!section.backgroundImage;
  return (
    <section
      style={{
        position: "relative",
        padding: "96px 24px",
        textAlign: "center",
        overflow: "hidden",
        ...(hasBgImage ? {
          backgroundImage: `linear-gradient(to bottom, ${t.bgColor}88, ${t.bgColor}dd), url(${section.backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        } : {}),
      }}
      data-testid="section-hero"
    >
      {!hasBgImage && (
        <div style={{
          position: "absolute", inset: 0,
          background: `radial-gradient(ellipse at 50% 0%, ${t.primaryColor}25 0%, transparent 70%)`,
        }} />
      )}
      <div style={{ position: "relative", maxWidth: 800, margin: "0 auto" }}>
        <h1
          style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.02em", color: t.txtColor, marginBottom: 20 }}
          data-testid="text-hero-title"
        >
          {section.title}
        </h1>
        {section.subtitle && (
          <p style={{ fontSize: "clamp(1rem, 2.5vw, 1.25rem)", color: t.mutedText, maxWidth: 600, margin: "0 auto 32px", lineHeight: 1.6 }} data-testid="text-hero-subtitle">
            {section.subtitle}
          </p>
        )}
        {section.ctaText && (
          <a
            href={section.ctaUrl || "#contact"}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "14px 32px", borderRadius: 8, fontWeight: 700,
              color: "#FFFFFF", backgroundColor: t.buttonColor,
              fontSize: "1.05rem", textDecoration: "none",
              boxShadow: `0 4px 20px ${t.buttonColor}40`,
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.transform = "translateY(-2px)"; }}
            onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.transform = "translateY(0)"; }}
            data-testid="button-hero-cta"
          >
            {section.ctaText}
            <ArrowRight size={18} />
          </a>
        )}
      </div>
    </section>
  );
}

function sectionBgStyle(section: LandingPageSection, t: PageTheme): React.CSSProperties {
  if (!section.backgroundImage) return {};
  return {
    backgroundImage: `linear-gradient(to bottom, ${t.bgColor}cc, ${t.bgColor}ee), url(${section.backgroundImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  };
}

function StatsSection({ section, t }: { section: LandingPageSection; t: PageTheme }) {
  return (
    <section style={{ padding: "64px 24px", ...sectionBgStyle(section, t) }} data-testid="section-stats">
      <div style={{ maxWidth: 1024, margin: "0 auto" }}>
        {section.title && <h2 style={{ fontSize: "1.5rem", fontWeight: 700, textAlign: "center", marginBottom: 40, color: t.txtColor }}>{section.title}</h2>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 20 }}>
          {section.items?.map((item, i) => (
            <div key={i} style={{
              textAlign: "center", padding: "28px 20px", borderRadius: 12,
              background: t.cardBg, border: `1px solid ${t.cardBorder}`,
            }}>
              <div style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)", fontWeight: 800, color: t.primaryColor, marginBottom: 4 }}>{item.stat}</div>
              <div style={{ fontSize: 14, color: t.mutedText }}>{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BenefitsSection({ section, t }: { section: LandingPageSection; t: PageTheme }) {
  return (
    <section style={{ padding: "80px 24px", ...sectionBgStyle(section, t) }} data-testid="section-benefits">
      <div style={{ maxWidth: 1024, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          {section.title && <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 700, color: t.txtColor, marginBottom: 12 }}>{section.title}</h2>}
          {section.subtitle && <p style={{ fontSize: 16, color: t.mutedText, maxWidth: 600, margin: "0 auto", lineHeight: 1.6 }}>{section.subtitle}</p>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: 24 }}>
          {section.items?.map((item, i) => (
            <div key={i} style={{
              padding: 28, borderRadius: 14,
              background: t.cardBg, border: `1px solid ${t.cardBorder}`,
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.transform = "translateY(-4px)"; (e.currentTarget as HTMLElement).style.boxShadow = `0 8px 30px ${t.primaryColor}15`; }}
            onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.transform = "translateY(0)"; (e.currentTarget as HTMLElement).style.boxShadow = "none"; }}
            >
              <div style={{
                width: 48, height: 48, borderRadius: 10,
                background: `${t.primaryColor}18`, color: t.primaryColor,
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 16,
              }}>
                {getIcon(item.icon, 22) || <Zap size={22} />}
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: t.txtColor, marginBottom: 8 }}>{item.title}</h3>
              <p style={{ fontSize: 14, color: t.mutedText, lineHeight: 1.7 }}>{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection({ section, t }: { section: LandingPageSection; t: PageTheme }) {
  return (
    <section style={{ padding: "80px 24px", ...sectionBgStyle(section, t) }} data-testid="section-features">
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          {section.title && <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 700, color: t.txtColor, marginBottom: 12 }}>{section.title}</h2>}
          {section.subtitle && <p style={{ fontSize: 16, color: t.mutedText }}>{section.subtitle}</p>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          {section.items?.map((item, i) => (
            <div key={i} style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
              <div style={{
                flexShrink: 0, width: 48, height: 48, borderRadius: "50%",
                background: `${t.primaryColor}18`, color: t.primaryColor,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 800, fontSize: 18,
              }}>
                {getIcon(item.icon, 20) || i + 1}
              </div>
              <div>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: t.txtColor, marginBottom: 4 }}>{item.title}</h3>
                <p style={{ fontSize: 14, color: t.mutedText, lineHeight: 1.7 }}>{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection({ section, t }: { section: LandingPageSection; t: PageTheme }) {
  return (
    <section style={{ padding: "80px 24px", ...sectionBgStyle(section, t) }} data-testid="section-testimonials">
      <div style={{ maxWidth: 1024, margin: "0 auto" }}>
        {section.title && <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 700, textAlign: "center", color: t.txtColor, marginBottom: 48 }}>{section.title}</h2>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: 24 }}>
          {section.items?.map((item, i) => (
            <div key={i} style={{
              padding: 28, borderRadius: 14,
              background: t.cardBg, border: `1px solid ${t.cardBorder}`,
              position: "relative",
            }}>
              <div style={{ position: "absolute", top: 16, right: 20, color: `${t.primaryColor}30` }}>
                <Quote size={32} />
              </div>
              <div style={{ display: "flex", gap: 6, color: "#F59E0B", marginBottom: 16 }}>
                {[1, 2, 3, 4, 5].map(s => <Star key={s} size={15} fill="currentColor" />)}
              </div>
              <p style={{ fontSize: 15, color: t.mutedText, lineHeight: 1.7, fontStyle: "italic", marginBottom: 20, position: "relative", zIndex: 1 }}>
                "{item.quote}"
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.name || ""} style={{
                    width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0,
                  }} />
                ) : (
                  <div style={{
                    width: 40, height: 40, borderRadius: "50%",
                    background: `${t.primaryColor}20`, color: t.primaryColor,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: 15, flexShrink: 0,
                  }}>
                    {(item.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: t.txtColor }}>{item.name}</div>
                  {item.role && <div style={{ fontSize: 12, color: t.mutedText }}>{item.role}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection({ section, t }: { section: LandingPageSection; t: PageTheme }) {
  return (
    <section style={{ padding: "80px 24px" }} data-testid="section-pricing">
      <div style={{ maxWidth: 1024, margin: "0 auto" }}>
        {section.title && <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 700, textAlign: "center", color: t.txtColor, marginBottom: 48 }}>{section.title}</h2>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24 }}>
          {section.items?.map((item, i) => {
            const featured = i === 1 || item.featured;
            return (
              <div key={i} style={{
                padding: 28, borderRadius: 14,
                background: featured ? `${t.primaryColor}12` : t.cardBg,
                border: `1px solid ${featured ? `${t.primaryColor}40` : t.cardBorder}`,
                transform: featured ? "scale(1.03)" : "none",
                boxShadow: featured ? `0 8px 30px ${t.primaryColor}20` : "none",
              }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: t.txtColor, marginBottom: 8 }}>{item.title}</h3>
                {item.price && <div style={{ fontSize: "2rem", fontWeight: 800, color: t.primaryColor, marginBottom: 12 }}>{item.price}</div>}
                <p style={{ fontSize: 14, color: t.mutedText, marginBottom: 20 }}>{item.description}</p>
                {item.features && (
                  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px" }}>
                    {item.features.map((f: string, fi: number) => (
                      <li key={fi} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: t.mutedText, marginBottom: 10 }}>
                        <CheckCircle size={15} color="#10B981" style={{ flexShrink: 0 }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                )}
                {item.url && (
                  <a href={item.url} style={{
                    display: "block", textAlign: "center", padding: "10px 0",
                    borderRadius: 8, fontWeight: 600,
                    background: featured ? t.buttonColor : "transparent",
                    color: featured ? "#FFF" : t.primaryColor,
                    border: featured ? "none" : `1px solid ${t.primaryColor}40`,
                    textDecoration: "none",
                  }}>
                    Get Started
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FaqSection({ section, t }: { section: LandingPageSection; t: PageTheme }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  return (
    <section style={{ padding: "80px 24px" }} data-testid="section-faq">
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        {section.title && <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 700, textAlign: "center", color: t.txtColor, marginBottom: 40 }}>{section.title}</h2>}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {section.items?.map((item, i) => (
            <div key={i} style={{ borderRadius: 12, border: `1px solid ${t.cardBorder}`, overflow: "hidden", background: openIdx === i ? t.cardBg : "transparent" }}>
              <button
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                style={{
                  width: "100%", textAlign: "left", padding: "18px 20px",
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
                  background: "transparent", border: "none", cursor: "pointer", color: t.txtColor,
                  fontSize: 15, fontWeight: 500,
                }}
                data-testid={`faq-toggle-${i}`}
              >
                <span>{item.question}</span>
                <ChevronRight size={16} style={{
                  transition: "transform 0.2s", flexShrink: 0, opacity: 0.4, color: t.txtColor,
                  transform: openIdx === i ? "rotate(90deg)" : "none",
                }} />
              </button>
              {openIdx === i && (
                <div style={{ padding: "0 20px 18px", fontSize: 14, color: t.mutedText, lineHeight: 1.7 }}>{item.answer}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LogoStripSection({ section, t }: { section: LandingPageSection; t: PageTheme }) {
  return (
    <section style={{ padding: "48px 24px" }} data-testid="section-logo-strip">
      <div style={{ maxWidth: 1024, margin: "0 auto" }}>
        {section.title && <h2 style={{ fontSize: 14, fontWeight: 600, textAlign: "center", color: t.mutedText, textTransform: "uppercase", letterSpacing: 2, marginBottom: 32 }}>{section.title}</h2>}
        <div style={{
          display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 32,
        }}>
          {section.items?.map((item, i) => (
            <div key={i} style={{ opacity: 0.6, transition: "opacity 0.2s" }}
              onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.opacity = "1"; }}
              onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.opacity = "0.6"; }}
            >
              {item.imageUrl ? (
                <img src={item.imageUrl} alt={item.title || ""} style={{ height: 36, maxWidth: 140, objectFit: "contain", filter: isColorDark(t.bgColor) ? "brightness(0) invert(1)" : "none" }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
              ) : (
                <span style={{ fontSize: 14, fontWeight: 600, color: t.mutedText }}>{item.title}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection({ section, t }: { section: LandingPageSection; t: PageTheme }) {
  return (
    <section style={{ padding: "80px 24px", ...sectionBgStyle(section, t) }} data-testid="section-cta">
      <div style={{
        maxWidth: 700, margin: "0 auto", textAlign: "center",
        padding: "56px 40px", borderRadius: 20,
        background: `linear-gradient(135deg, ${t.primaryColor}18, ${t.primaryColor}08)`,
        border: `1px solid ${t.primaryColor}25`,
      }}>
        {section.title && <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 700, color: t.txtColor, marginBottom: 16 }}>{section.title}</h2>}
        {section.subtitle && <p style={{ fontSize: 16, color: t.mutedText, marginBottom: 28 }}>{section.subtitle}</p>}
        {section.ctaText && (
          <a
            href={section.ctaUrl || "#contact"}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "14px 32px", borderRadius: 8, fontWeight: 700,
              color: "#FFFFFF", backgroundColor: t.buttonColor,
              fontSize: "1.05rem", textDecoration: "none",
              boxShadow: `0 4px 20px ${t.buttonColor}40`,
              transition: "transform 0.2s",
            }}
            onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.transform = "translateY(-2px)"; }}
            onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.transform = "translateY(0)"; }}
            data-testid="button-cta-bottom"
          >
            {section.ctaText}
            <ArrowRight size={18} />
          </a>
        )}
      </div>
    </section>
  );
}

/**
 * "Want to pick a time now?" after a page's form is sent (server/booking/booking-offer.ts): the next open times, each opening the booking
 * page on that time with the visitor's details filled in. The same panel websites get from /embed/drpr-forms.js.
 */
function BookingOfferPanel({ offer, firstName }: { offer: any; firstName?: string }) {
  const color = /^#[0-9a-f]{6}$/i.test(String(offer?.color || "")) ? offer.color : "#2563eb";
  const slots: Array<{ label: string; url: string }> = Array.isArray(offer?.slots) ? offer.slots : [];
  return (
    <div data-testid="booking-offer-panel" style={{ marginTop: 20, padding: 20, borderRadius: 12, background: "#ffffff", color: "#111827", textAlign: "left", border: "1px solid #e5e7eb" }}>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{firstName ? `Thanks, ${firstName}! ` : "Thanks! "}Want to pick a time now?</div>
      <div style={{ fontSize: 14, color: "#4b5563", marginBottom: 14 }}>{offer?.title || "Call"} with {offer?.business} ({offer?.durationMins || 30} min, {offer?.zoneLabel})</div>
      {slots.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          {slots.map((sl) => (
            <a key={sl.url} href={sl.url} data-testid="booking-offer-slot" style={{ padding: "10px 14px", borderRadius: 8, background: color, color: "#ffffff", textDecoration: "none", fontWeight: 600, fontSize: 14 }}>{sl.label}</a>
          ))}
        </div>
      )}
      {offer?.url && <a href={offer.url} style={{ fontSize: 14, fontWeight: 600, color, textDecoration: "underline" }}>{slots.length ? "See all available times" : "Pick a time that suits you"}</a>}
      {offer?.note && <div style={{ fontSize: 12, color: "#6b7280", marginTop: 10 }}>{offer.note}</div>}
    </div>
  );
}

function ContactFormSection({ section, slug, t }: { section: LandingPageSection; slug: string; t: PageTheme }) {
  const [bookingOffer, setBookingOffer] = useState<any>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handleSubmit = async () => {
    const errors: Record<string, string> = {};
    if (!email.trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errors.email = "Enter a valid email";
    if (!name.trim()) errors.name = "Name is required";
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const utm: Record<string, string> = {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_content"].forEach(k => {
        const v = urlParams.get(k);
        if (v) utm[k] = v;
      });
      const resp = await fetch(`/api/public/pages/${slug}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: { email: email.trim(), name: name.trim(), phone: phone.trim() || undefined, message: message.trim() || undefined }, utm }),
      });
      if (resp.ok) {
        // "pick a time now" (server/booking/booking-offer.ts): the submit answers with the next open times when booking is on
        const j = await resp.json().catch(() => null);
        if (j?.booking) setBookingOffer(j.booking);
        setSubmitted(true);
      } else {
        setFormErrors({ email: "Something went wrong. Please try again." });
      }
    } catch {
      setFormErrors({ email: "Network error. Please try again." });
    }
    setSubmitting(false);
  };

  const inputStyle = {
    width: "100%", padding: "14px 16px", borderRadius: 10,
    border: `1px solid ${t.inputBorder}`, background: t.inputBg,
    color: t.txtColor, fontSize: 14, outline: "none",
    fontFamily: "inherit",
  };

  if (submitted) {
    return (
      <section id="contact" style={{ padding: "80px 24px" }} data-testid="section-contact-form-success">
        <div style={{
          maxWidth: 440, margin: "0 auto", textAlign: "center",
          padding: 40, borderRadius: 16,
          background: "#10B98118", border: "1px solid #10B98130",
        }}>
          <CheckCircle size={44} color="#10B981" style={{ margin: "0 auto 16px" }} />
          <h2 style={{ fontSize: 22, fontWeight: 700, color: t.txtColor, marginBottom: 8 }}>Thank You!</h2>
          <p style={{ color: t.mutedText }}>We've received your information and will be in touch soon.</p>
          {bookingOffer && <BookingOfferPanel offer={bookingOffer} firstName={name.trim().split(/\s+/)[0]} />}
        </div>
      </section>
    );
  }

  return (
    <section id="contact" style={{ padding: "80px 24px" }} data-testid="section-contact-form">
      <div style={{
        maxWidth: 440, margin: "0 auto",
        padding: 36, borderRadius: 16,
        background: t.cardBg, border: `1px solid ${t.cardBorder}`,
      }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          {section.title && <h2 style={{ fontSize: 22, fontWeight: 700, color: t.txtColor, marginBottom: 6 }}>{section.title}</h2>}
          {section.subtitle && <p style={{ fontSize: 14, color: t.mutedText }}>{section.subtitle}</p>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <input type="text" value={name} onChange={(e) => { setName(e.target.value); setFormErrors(fe => ({ ...fe, name: "" })); }}
              placeholder="Your name" style={inputStyle} data-testid="input-contact-name" />
            {formErrors.name && <p style={{ fontSize: 12, color: "#EF4444", marginTop: 4 }} data-testid="error-contact-name">{formErrors.name}</p>}
          </div>
          <div>
            <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setFormErrors(fe => ({ ...fe, email: "" })); }}
              placeholder="Your email" style={inputStyle} data-testid="input-contact-email" />
            {formErrors.email && <p style={{ fontSize: 12, color: "#EF4444", marginTop: 4 }} data-testid="error-contact-email">{formErrors.email}</p>}
          </div>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone (optional)" style={inputStyle} data-testid="input-contact-phone" />
          <textarea value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="Your message (optional)" rows={3}
            style={{ ...inputStyle, resize: "none" as const }} data-testid="input-contact-message" />
          <button onClick={handleSubmit} disabled={submitting}
            style={{
              width: "100%", padding: "14px 0", borderRadius: 10,
              fontWeight: 700, color: "#FFFFFF", background: t.buttonColor,
              border: "none", cursor: submitting ? "wait" : "pointer",
              fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              opacity: submitting ? 0.6 : 1,
              boxShadow: `0 4px 16px ${t.buttonColor}30`,
              fontFamily: "inherit",
            }}
            data-testid="button-contact-submit">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {submitting ? "Sending..." : section.ctaText || "Get in Touch"}
          </button>
        </div>
      </div>
    </section>
  );
}

function renderSection(section: LandingPageSection, slug: string | undefined, t: PageTheme) {
  switch (section.type) {
    case "hero": return <HeroSection key={section.id} section={section} t={t} />;
    case "stats": return <StatsSection key={section.id} section={section} t={t} />;
    case "benefits": return <BenefitsSection key={section.id} section={section} t={t} />;
    case "features": return <FeaturesSection key={section.id} section={section} t={t} />;
    case "testimonials": return <TestimonialsSection key={section.id} section={section} t={t} />;
    case "pricing": return <PricingSection key={section.id} section={section} t={t} />;
    case "faq": return <FaqSection key={section.id} section={section} t={t} />;
    case "cta": return <CtaSection key={section.id} section={section} t={t} />;
    case "logo_strip": return <LogoStripSection key={section.id} section={section} t={t} />;
    case "contact_form": return slug ? <ContactFormSection key={section.id} section={section} slug={slug} t={t} /> : <CtaSection key={section.id} section={section} t={t} />;
    default: return null;
  }
}

function renderMarkdown(md: string) {
  const lines = md.split("\n");
  const elements: JSX.Element[] = [];
  let listItems: string[] = [];
  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(<ul key={`ul-${elements.length}`} className="list-disc pl-6 space-y-1 text-sm opacity-70">{listItems.map((li, i) => <li key={i}>{li}</li>)}</ul>);
      listItems = [];
    }
  };
  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("# ")) { flushList(); elements.push(<h1 key={idx} className="text-2xl font-bold mt-6 mb-2">{trimmed.slice(2)}</h1>); }
    else if (trimmed.startsWith("## ")) { flushList(); elements.push(<h2 key={idx} className="text-xl font-bold mt-5 mb-2">{trimmed.slice(3)}</h2>); }
    else if (trimmed.startsWith("### ")) { flushList(); elements.push(<h3 key={idx} className="text-lg font-semibold mt-4 mb-1">{trimmed.slice(4)}</h3>); }
    else if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) { listItems.push(trimmed.slice(2)); }
    else if (/^\d+\.\s/.test(trimmed)) { listItems.push(trimmed.replace(/^\d+\.\s/, "")); }
    else if (trimmed === "") { flushList(); }
    else { flushList(); elements.push(<p key={idx} className="text-sm opacity-70 leading-relaxed">{trimmed}</p>); }
  });
  flushList();
  return <>{elements}</>;
}

function GatedContentPage({ pageData: pd, theme, slug, settings }: { pageData: LandingPageData; theme?: any; slug: string; settings?: LandingPageSettings }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const primaryColor = theme?.primaryColor || "#3B82F6";
  const bgColor = theme?.backgroundColor || "#0A0A0F";
  const txtColor = theme?.textColor || "#FFFFFF";

  const handleSubmit = async () => {
    const errors: Record<string, string> = {};
    if (!email.trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errors.email = "Enter a valid email";
    if (!name.trim()) errors.name = "Name is required";
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const utm: Record<string, string> = {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_content"].forEach(k => {
        const v = urlParams.get(k);
        if (v) utm[k] = v;
      });
      const resp = await fetch(`/api/public/pages/${slug}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: { email: email.trim(), name: name.trim(), phone: phone.trim() || undefined }, utm }),
      });
      if (resp.ok) {
        setRevealed(true);
      } else {
        setFormErrors({ email: "Something went wrong. Please try again." });
      }
    } catch {
      setFormErrors({ email: "Network error. Please try again." });
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: bgColor, color: txtColor }} data-testid="public-gated-page">
      {theme?.brandName && (
        <header className="sticky top-0 z-50 backdrop-blur-md border-b border-white/[0.06]" style={{ backgroundColor: `${bgColor}99` }}>
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
            {theme.logoUrl && <img src={theme.logoUrl} alt="" className="w-8 h-8 rounded-md object-cover" />}
            <span className="font-bold text-lg">{theme.brandName}</span>
          </div>
        </header>
      )}

      {pd.sections?.map(section => renderSection(section, slug, buildTheme(theme)))}

      {!revealed ? (
        <section id="download" style={{ padding: "80px 24px" }} data-testid="gated-form-section">
          <div style={{ maxWidth: 440, margin: "0 auto", padding: 32, borderRadius: 16, border: `1px solid ${txtColor}15`, background: `${primaryColor}10` }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: txtColor, marginBottom: 6 }}>Get Your Free Guide</h2>
              <p style={{ fontSize: 14, color: `${txtColor}99` }}>Enter your details below to unlock the full content.</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setFormErrors(fe => ({ ...fe, name: "" })); }}
                  placeholder="Your name"
                  style={{ width: "100%", padding: "14px 16px", borderRadius: 10, border: `1px solid ${txtColor}15`, background: `${txtColor}08`, color: txtColor, fontSize: 14, outline: "none", fontFamily: "inherit" }}
                  data-testid="input-gated-name"
                />
                {formErrors.name && <p style={{ fontSize: 12, color: "#EF4444", marginTop: 4 }} data-testid="error-gated-name">{formErrors.name}</p>}
              </div>
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setFormErrors(fe => ({ ...fe, email: "" })); }}
                  placeholder="Your email"
                  style={{ width: "100%", padding: "14px 16px", borderRadius: 10, border: `1px solid ${txtColor}15`, background: `${txtColor}08`, color: txtColor, fontSize: 14, outline: "none", fontFamily: "inherit" }}
                  data-testid="input-gated-email"
                />
                {formErrors.email && <p style={{ fontSize: 12, color: "#EF4444", marginTop: 4 }} data-testid="error-gated-email">{formErrors.email}</p>}
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone (optional)"
                style={{ width: "100%", padding: "14px 16px", borderRadius: 10, border: `1px solid ${txtColor}15`, background: `${txtColor}08`, color: txtColor, fontSize: 14, outline: "none", fontFamily: "inherit" }}
                data-testid="input-gated-phone"
              />
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  width: "100%", padding: "14px 0", borderRadius: 10,
                  fontWeight: 700, color: "#FFF", background: primaryColor,
                  border: "none", cursor: submitting ? "wait" : "pointer",
                  fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  opacity: submitting ? 0.6 : 1, fontFamily: "inherit",
                  boxShadow: `0 4px 16px ${primaryColor}30`,
                }}
                data-testid="button-gated-submit"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                {submitting ? "Unlocking..." : "Get Free Access"}
              </button>
            </div>
          </div>
        </section>
      ) : (
        <section style={{ padding: "64px 24px" }} data-testid="gated-content-revealed">
          <div style={{ maxWidth: 700, margin: "0 auto", padding: 32, borderRadius: 16, border: `1px solid ${txtColor}15`, background: `${txtColor}04` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#10B981", marginBottom: 16 }}>
              <CheckCircle size={18} />
              <span style={{ fontSize: 14, fontWeight: 600 }}>Content unlocked!</span>
            </div>
            {pd.gatedContent && renderMarkdown(pd.gatedContent)}
          </div>
        </section>
      )}

      <footer style={{ padding: "32px 24px", borderTop: `1px solid ${txtColor}10`, textAlign: "center" }}>
        <p style={{ fontSize: 12, opacity: 0.3, color: txtColor }}>&copy; {new Date().getFullYear()} {theme?.brandName || ""}. All rights reserved.</p>
      </footer>
    </div>
  );
}

function FunnelRenderer({ steps, theme, slug, settings }: { steps: FunnelStep[]; theme?: any; slug: string; settings?: LandingPageSettings }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [bookingOffer, setBookingOffer] = useState<any>(null);

  const step = steps[currentStep];
  if (!step) return null;

  const progress = ((currentStep + 1) / steps.length) * 100;

  const canAdvance = () => {
    if (step.type === "single_select") return !!answers[step.id];
    if (step.type === "multi_select") return Array.isArray(answers[step.id]) && answers[step.id].length > 0;
    if (step.type === "text_input") return !!answers[step.id];
    if (step.type === "contact_form") {
      const required = step.fields?.filter(f => f.required) || [];
      return required.every(f => formData[f.name]?.trim());
    }
    return true;
  };

  const handleNext = async () => {
    if (step.type === "contact_form") {
      setSubmitting(true);
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const utm: Record<string, string> = {};
        ["utm_source", "utm_medium", "utm_campaign", "utm_content"].forEach(k => {
          const v = urlParams.get(k);
          if (v) utm[k] = v;
        });
        const resp = await fetch(`/api/public/pages/${slug}/submit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ data: { ...answers, ...formData }, utm }),
        });
        const j = resp.ok ? await resp.json().catch(() => null) : null;
        if (j?.booking) setBookingOffer(j.booking);
        setSubmitted(true);
      } catch { }
      setSubmitting(false);
      return;
    }
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const primaryColor = theme?.primaryColor || "#3B82F6";

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: theme?.backgroundColor || "#0A0A0F", color: theme?.textColor || "#FFF" }}>
        <div className="text-center space-y-4 max-w-md" data-testid="funnel-success">
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
            <CheckCircle size={32} style={{ color: primaryColor }} />
          </div>
          <h2 className="text-2xl font-bold">{settings?.thankYouMessage || "Thank you!"}</h2>
          <p className="opacity-60">We'll be in touch soon.</p>
          {bookingOffer && <BookingOfferPanel offer={bookingOffer} firstName={String(formData.first_name || formData.firstName || formData.name || "").trim().split(/\s+/)[0]} />}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: theme?.backgroundColor || "#0A0A0F", color: theme?.textColor || "#FFF" }} data-testid="funnel-renderer">
      <div className="w-full h-1 bg-foreground/10">
        <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: primaryColor }} />
      </div>

      {theme?.brandName && (
        <div className="text-center py-6">
          <div className="text-lg font-bold opacity-80">{theme.brandName}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 max-w-lg mx-auto w-full">
        <div className="w-full space-y-8">
          <div className="space-y-2 text-center">
            <h2 className="text-xl md:text-2xl font-bold" data-testid="text-funnel-step-title">{step.title}</h2>
            {step.subtitle && <p className="text-sm opacity-50">{step.subtitle}</p>}
            {step.description && <p className="text-sm opacity-60">{step.description}</p>}
          </div>

          {step.type === "single_select" && step.options && (
            <div className="space-y-3" data-testid="funnel-single-select">
              {step.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => setAnswers({ ...answers, [step.id]: opt.value })}
                  className={`w-full text-left px-5 py-4 rounded-lg border transition-colors flex items-center gap-3 ${
                    answers[step.id] === opt.value
                      ? "border-blue-500/50 bg-blue-600/10"
                      : "border-foreground/10 hover:border-foreground/20 bg-foreground/[0.03]"
                  }`}
                  data-testid={`option-${opt.value}`}
                >
                  {opt.icon && <span className="text-blue-400">{getIcon(opt.icon, 18)}</span>}
                  <span className="flex-1 font-medium text-sm">{opt.label}</span>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    answers[step.id] === opt.value ? "border-blue-500" : "border-foreground/20"
                  }`}>
                    {answers[step.id] === opt.value && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                  </div>
                </button>
              ))}
            </div>
          )}

          {step.type === "multi_select" && step.options && (
            <div className="space-y-3" data-testid="funnel-multi-select">
              {step.options.map((opt, i) => {
                const selected = (answers[step.id] || []).includes(opt.value);
                return (
                  <button
                    key={i}
                    onClick={() => {
                      const current = answers[step.id] || [];
                      setAnswers({
                        ...answers,
                        [step.id]: selected ? current.filter((v: string) => v !== opt.value) : [...current, opt.value],
                      });
                    }}
                    className={`w-full text-left px-5 py-4 rounded-lg border transition-colors flex items-center gap-3 ${
                      selected ? "border-blue-500/50 bg-blue-600/10" : "border-foreground/10 hover:border-foreground/20 bg-foreground/[0.03]"
                    }`}
                    data-testid={`option-multi-${opt.value}`}
                  >
                    {opt.icon && <span className="text-blue-400">{getIcon(opt.icon, 18)}</span>}
                    <span className="flex-1 font-medium text-sm">{opt.label}</span>
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                      selected ? "border-blue-500 bg-blue-500" : "border-foreground/20"
                    }`}>
                      {selected && <CheckCircle size={12} className="text-white" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {step.type === "text_input" && (
            <div data-testid="funnel-text-input">
              <textarea
                value={answers[step.id] || ""}
                onChange={(e) => setAnswers({ ...answers, [step.id]: e.target.value })}
                placeholder="Your answer"
                className="w-full px-4 py-3 rounded-lg border border-foreground/10 bg-foreground/[0.03] text-foreground placeholder:opacity-30 resize-none min-h-[120px] focus:border-blue-500/50 focus:outline-none"
                data-testid="input-text-answer"
              />
            </div>
          )}

          {step.type === "info_screen" && step.infoBlocks && (
            <div className="space-y-4" data-testid="funnel-info-screen">
              {step.infoBlocks.map((block, i) => (
                <div key={i} className="flex gap-4 items-start p-4 rounded-lg bg-foreground/[0.03] border border-foreground/[0.06]">
                  <div className="text-blue-400 flex-shrink-0 mt-0.5">{getIcon(block.icon, 20) || <CheckCircle size={20} />}</div>
                  <div>
                    <div className="font-semibold text-sm">{block.title}</div>
                    <div className="text-xs opacity-50 mt-1">{block.description}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {step.type === "contact_form" && step.fields && (
            <div className="space-y-4" data-testid="funnel-contact-form">
              {step.fields.map((field, i) => (
                <div key={i}>
                  <label className="block text-xs font-medium opacity-50 mb-1.5">{field.label}{field.required && " *"}</label>
                  {field.type === "textarea" ? (
                    <textarea
                      value={formData[field.name] || ""}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      placeholder={field.placeholder}
                      className="w-full px-4 py-3 rounded-lg border border-foreground/10 bg-foreground/[0.03] text-foreground placeholder:opacity-30 resize-none min-h-[80px] focus:border-blue-500/50 focus:outline-none text-sm"
                      data-testid={`input-${field.name}`}
                    />
                  ) : field.type === "select" && field.options ? (
                    <select
                      value={formData[field.name] || ""}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-foreground/10 bg-foreground/[0.03] text-foreground focus:border-blue-500/50 focus:outline-none text-sm"
                      data-testid={`select-${field.name}`}
                    >
                      <option value="">Select...</option>
                      {field.options.map((opt, oi) => <option key={oi} value={opt}>{opt}</option>)}
                    </select>
                  ) : (
                    <input
                      type={field.type === "email" ? "email" : field.type === "phone" ? "tel" : "text"}
                      value={formData[field.name] || ""}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      placeholder={field.placeholder}
                      className="w-full px-4 py-3 rounded-lg border border-foreground/10 bg-foreground/[0.03] text-foreground placeholder:opacity-30 focus:border-blue-500/50 focus:outline-none text-sm"
                      data-testid={`input-${field.name}`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3">
            {currentStep > 0 && (
              <button
                onClick={handleBack}
                className="px-5 py-3 rounded-lg border border-foreground/10 text-sm font-medium opacity-60 hover:opacity-100 transition-opacity flex items-center gap-2"
                data-testid="button-funnel-back"
              >
                <ChevronLeft size={16} />
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!canAdvance() || submitting}
              className="flex-1 py-3 rounded-lg font-semibold text-white transition-colors disabled:opacity-30 flex items-center justify-center gap-2"
              style={{ backgroundColor: canAdvance() && !submitting ? primaryColor : `${primaryColor}40` }}
              data-testid="button-funnel-next"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
              {step.ctaText || (step.type === "contact_form" ? "Submit" : "Continue")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// M6.3: floating sales-chatbot widget rendered on public funnel pages. Uses the
// siteAgent surfaced by /api/public/pages/:slug and round-trips to /api/widget/:wsId/chat.
function SiteAgentWidget({ siteAgent }: { siteAgent: any }) {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<{ role: "user" | "agent"; text: string }[]>([]);
  if (!siteAgent || !siteAgent.workspaceId) return null;
  const color = siteAgent.primaryColor || "#2563EB";
  const greeting = siteAgent.greeting || `Hi! Ask me anything about ${siteAgent.agentName || "us"}.`;

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages(m => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const r = await fetch(`/api/widget/${siteAgent.workspaceId}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, message: text, pageUrl: typeof window !== "undefined" ? window.location.href : "" }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.sessionId) setSessionId(d.sessionId);
      setMessages(m => [...m, { role: "agent", text: d.reply || d.message || "Sorry, I couldn't process that just now." }]);
    } catch {
      setMessages(m => [...m, { role: "agent", text: "Sorry — I'm having trouble connecting right now." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9998, fontFamily: "Inter, sans-serif" }} data-testid="site-agent-widget">
      {open && (
        <div style={{ width: 340, maxWidth: "calc(100vw - 40px)", height: 460, maxHeight: "70vh", background: "#fff", color: "#111", borderRadius: 16, boxShadow: "0 12px 40px rgba(0,0,0,0.25)", display: "flex", flexDirection: "column", overflow: "hidden", marginBottom: 12 }} data-testid="site-agent-panel">
          <div style={{ background: color, color: "#fff", padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{siteAgent.agentName || "Chat with us"}</span>
            <button onClick={() => setOpen(false)} style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", display: "flex" }} data-testid="site-agent-close"><X size={18} /></button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10, background: "#f7f7f9" }}>
            <div style={{ alignSelf: "flex-start", background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: "9px 12px", fontSize: 14, maxWidth: "85%" }}>{greeting}</div>
            {messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", background: m.role === "user" ? color : "#fff", color: m.role === "user" ? "#fff" : "#111", border: m.role === "user" ? "none" : "1px solid #eee", borderRadius: 12, padding: "9px 12px", fontSize: 14, maxWidth: "85%", whiteSpace: "pre-wrap" }}>{m.text}</div>
            ))}
            {sending && <div style={{ alignSelf: "flex-start", opacity: 0.5, fontSize: 13, padding: "0 4px" }}>typing…</div>}
          </div>
          <div style={{ display: "flex", gap: 8, padding: 12, borderTop: "1px solid #eee", background: "#fff" }}>
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") send(); }} placeholder="Type a message…" style={{ flex: 1, border: "1px solid #ddd", borderRadius: 10, padding: "9px 12px", fontSize: 14, outline: "none", color: "#111" }} data-testid="site-agent-input" />
            <button onClick={send} disabled={sending} style={{ background: color, color: "#fff", border: "none", borderRadius: 10, padding: "0 12px", cursor: "pointer", display: "flex", alignItems: "center" }} data-testid="site-agent-send"><Send size={16} /></button>
          </div>
        </div>
      )}
      <button onClick={() => setOpen(o => !o)} style={{ width: 56, height: 56, borderRadius: "50%", background: color, color: "#fff", border: "none", boxShadow: "0 6px 20px rgba(0,0,0,0.3)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "auto" }} data-testid="site-agent-bubble" aria-label="Chat">
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>
    </div>
  );
}

export default function PublicPage() {
  const [, routeParams] = useRoute("/p/:slug");
  const slug = routeParams?.slug ||
    (typeof window !== "undefined" ? window.location.pathname.match(/^\/p\/(.+)$/)?.[1] : undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageData, setPageData] = useState<any>(null);

  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const isPreview = urlParams.get("preview") === "1";
  const previewId = urlParams.get("previewId");
  const previewWsId = urlParams.get("wsId");

  useEffect(() => {
    if (!slug) return;
    if (isPreview && previewId && previewWsId) {
      fetch(`/api/workspaces/${previewWsId}/pages/${previewId}/preview-data`, { credentials: "include" })
        .then(r => {
          if (!r.ok) throw new Error("Preview unavailable — you may need to log in first.");
          return r.json();
        })
        .then(setPageData)
        .catch(e => setError(e.message))
        .finally(() => setLoading(false));
    } else {
      fetch(`/api/public/pages/${slug}`)
        .then(r => {
          if (!r.ok) throw new Error("Page not found");
          return r.json();
        })
        .then(setPageData)
        .catch(e => setError(e.message))
        .finally(() => setLoading(false));
    }
  }, [slug, isPreview, previewId, previewWsId]);

  // MUST be before any early returns — React hooks cannot be called after conditional returns
  useEffect(() => {
    if (!pageData) return;
    const pd = pageData.pageData || {};
    const theme = pd.theme;
    const title = pageData.title || theme?.brandName || "Page";
    document.title = title;
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) { el = document.createElement("meta"); (name.startsWith("og:") ? el.setAttribute("property", name) : el.setAttribute("name", name)); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const heroSection = pd.sections?.find((s: any) => s.type === "hero");
    const desc = heroSection?.subtitle || pageData.title || "";
    setMeta("description", desc);
    setMeta("og:title", title);
    setMeta("og:description", desc);
    setMeta("og:type", "website");
  }, [pageData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center space-y-3">
          <h1 className="text-2xl font-bold">Page Not Found</h1>
          <p className="opacity-50 text-sm">This page doesn't exist or isn't published yet.</p>
        </div>
      </div>
    );
  }

  const pd: LandingPageData = pageData.pageData || {};
  const settings: LandingPageSettings = pageData.settings || {};
  const theme = pd.theme;
  const bgColor = theme?.backgroundColor || "#0A0A0F";
  const txtColor = theme?.textColor || "#FFFFFF";

  const backToEditorUrl = previewId
    ? `/content?tab=pages&editPage=${previewId}`
    : "/content?tab=pages";

  const draftBanner = isPreview && pageData.status !== "published" ? (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-black text-center text-xs font-semibold py-2 px-4 flex items-center justify-center gap-2" data-testid="banner-draft-preview">
      <span>⚠️ Draft Preview — this page is not yet published</span>
      <a href={backToEditorUrl} className="underline font-bold">Back to editor</a>
    </div>
  ) : null;

  const isEmpty = !pd.sections?.length && !pd.steps?.length && !pd.gatedContent;

  if (isEmpty && isPreview) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] text-white flex flex-col" data-testid="public-page-empty-preview">
        {draftBanner}
        <div className="flex-1 flex items-center justify-center pt-10">
          <div className="text-center space-y-4 max-w-md p-8">
            <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto">
              <Sparkles size={28} className="text-blue-400" />
            </div>
            <h2 className="text-xl font-bold">No content yet</h2>
            <p className="text-sm text-white/50">This page is empty. Add sections in the Page Builder to see them here.</p>
            <a
              href={backToEditorUrl}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors"
              data-testid="link-empty-preview-edit"
            >
              Open in Page Builder
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (pageData.type === "funnel" && pd.steps) {
    return <>{draftBanner}<FunnelRenderer steps={pd.steps} theme={theme} slug={slug!} settings={settings} /><SiteAgentWidget siteAgent={pageData.siteAgent} /></>;
  }

  if (pd.gatedContent) {
    return <>{draftBanner}<GatedContentPage pageData={pd} theme={theme} slug={slug!} settings={settings} /><SiteAgentWidget siteAgent={pageData.siteAgent} /></>;
  }

  const fontFamily = theme?.fontFamily || "Inter, sans-serif";
  const t = buildTheme(theme);

  return (
    <div style={{ backgroundColor: bgColor, color: txtColor, fontFamily, minHeight: "100vh" }} data-testid="public-landing-page">
      {draftBanner}
      {theme?.brandName && (
        <header style={{ backgroundColor: `${bgColor}cc`, backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 50, borderBottom: `1px solid ${txtColor}10` }}>
          <div style={{ maxWidth: 1152, margin: "0 auto", padding: "16px 24px", display: "flex", alignItems: "center", gap: 12 }}>
            {theme.logoUrl && <img src={theme.logoUrl} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: "cover" }} onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />}
            <span style={{ fontWeight: 700, fontSize: 18, color: txtColor }}>{theme.brandName}</span>
          </div>
        </header>
      )}
      <div style={{ color: txtColor }}>
        {pd.sections?.map(section => (
          <div key={section.id} style={{ color: txtColor }}>
            {renderSection(section, slug!, t)}
          </div>
        ))}
      </div>
      <footer style={{ padding: "32px 24px", borderTop: `1px solid ${txtColor}15`, textAlign: "center" }}>
        <p style={{ fontSize: 12, opacity: 0.3, color: txtColor }}>&copy; {new Date().getFullYear()} {theme?.brandName || ""}. All rights reserved.</p>
      </footer>
      <SiteAgentWidget siteAgent={pageData.siteAgent} />
    </div>
  );
}